import React, { useState, useEffect } from 'react';
import { Sparkles, CheckCircle, Star, Mail, Phone, MapPin } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import TopHeader from './components/TopHeader';

export default function HomePage() {
  const [scrollY, setScrollY] = useState(0);
  console.log(scrollY)
  
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const packages = [
    {
      name: 'The Refresh',
      price: '$180',
      period: '/month',
      description: '4 bathroom-focused visits per month',
      features: [
        'Sink, toilet, shower, and tub scrubbed and disinfected',
        'Mirrors polished to a shine',
        'Floors washed and sanitized to professional standards',
        'Premium eco-friendly supplies included'
      ],
      icon: '🛁',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'The Transformation',
      price: 'from $250',
      period: '/month',
      description: '4 full-home visits per month',
      features: [
        'Full-home dusting, vacuuming, and sanitization',
        'Kitchens and bathrooms detailed to perfection',
        'Trash removal and general tidy-up',
        'Add-ons: inside fridge, oven deep clean, or laundry service'
      ],
      icon: '🏠',
      color: 'from-purple-500 to-pink-500',
      popular: true
    },
    {
      name: 'The Grand Slam',
      price: 'from $75',
      period: '/visit',
      description: 'One-time deep clean perfection',
      features: [
        'Perfect for move-ins, move-outs, or annual refresh',
        'Complete deep cleaning of entire space',
        'Makes everything feel brand new again',
        'No commitment required'
      ],
      icon: '✨',
      color: 'from-amber-500 to-orange-500'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Choose Your Service',
      description: 'Whether it\'s a quick bathroom refresh, a deep kitchen detail, or a full-home transformation—we price transparently and clean meticulously.'
    },
    {
      number: '2',
      title: 'Pick Your Time',
      description: 'Morning, noon, or evening—we work around your life. Simply choose your preferred date and time.'
    },
    {
      number: '3',
      title: 'Sit Back and Smile',
      description: 'Confirm instantly, then relax. Your spotless space is on its way.'
    }
  ];

  const testimonials = [
    {
      text: 'Our weekends are finally ours again. Always on time, always perfect.',
      author: 'Nadia M.',
      location: 'Ottawa'
    },
    {
      text: 'I thought I knew clean—until Esther\'s team arrived.',
      author: 'Kevin T.',
      location: 'Orleans'
    }
  ];

  const trustPoints = [
    { icon: CheckCircle, text: 'Precision Cleaners: Vetted, detail-obsessed professionals' },
    { icon: CheckCircle, text: 'Eco-Conscious: Safe for your family, pets, and planet' },
    { icon: CheckCircle, text: 'Reliable & Punctual: We show up—on time, every time' },
    { icon: CheckCircle, text: 'Transparent Pricing: No surprises. Ever' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header */}
      <TopHeader showBookNow />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <h2 className="text-5xl lg:text-6xl font-bold leading-tight text-gray-900">
                The only thing brighter than your home will be your{' '}
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  peace of mind
                </span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed !mb-4">
                Stop stressing about mess. From weekly refreshes that sparkle to deep cleans that transform, EstherSpotless delivers perfection—professionally, reliably, and with care.
              </p>
              <NavLink to="/book">
              <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition-all transform hover:scale-105 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Book Your Spotless Clean
              </button>
              </NavLink>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
              <div className="relative rounded-3xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all">
                <img
                  src="/images/jeshoots-com-__ZMnefoI3k-unsplash.jpg"
                  alt="Spotless clean home interior"
                  className="w-full h-full object-cover rounded-3xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent flex items-end justify-center p-8">
                  <div className="text-center text-white">
                    <Sparkles className="w-16 h-16 mx-auto mb-2 drop-shadow-lg" />
                    <p className="text-2xl font-semibold drop-shadow-lg">Your Spotless Space Awaits</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Flawless in 3 Easy Steps</h2>
            <p className="text-xl text-gray-600">Book Your Clean in Just 60 Seconds</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
                <div className="absolute -top-6 left-8 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-xl text-gray-700 italic mb-6">"Your next spotless home is only three clicks away."</p>
            <NavLink to="/book">
            <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition-all transform hover:scale-105">
              Start Booking Now
            </button>
            </NavLink>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">See the Difference</h2>
            <p className="text-xl text-gray-600">Experience the transformation we bring to every home</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <img
                src="/images/jeshoots-com-__ZMnefoI3k-unsplash.jpg"
                alt="Pristine clean living space"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <p className="text-white text-lg font-semibold">Pristine Living Spaces</p>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <img
                src="/images/minh-pham-OtXADkUh3-I-unsplash.jpg"
                alt="Sparkling clean kitchen"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <p className="text-white text-lg font-semibold">Sparkling Kitchens</p>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 md:col-span-2 lg:col-span-1">
              <img
                src="/images/premium_photo-1678304223767-b9aa73c1e147.avif"
                alt="Immaculate home details"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <p className="text-white text-lg font-semibold">Attention to Detail</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">The Price of Perfection</h2>
            <p className="text-xl text-gray-600">Simple. Transparent. Commitment-free.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div key={index} className={`relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 ${pkg.popular ? 'ring-4 ring-purple-500' : ''}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <div className="text-5xl mb-4">{pkg.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">{pkg.price}</span>
                  <span className="text-gray-600">{pkg.period}</span>
                </div>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <NavLink to="/book">
                <button className={`w-full bg-gradient-to-r ${pkg.color} text-white py-3 rounded-full font-semibold hover:shadow-lg transition-all transform hover:scale-105`}>
                  View Available Dates
                </button>
                </NavLink>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Sparkles className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-6">The EstherSpotless Guarantee</h2>
          <p className="text-2xl mb-4">If it's not perfect, we're not finished.</p>
          <p className="text-xl leading-relaxed opacity-90">
            We treat your home like our own—with care, precision, and pride. If you're ever less than thrilled, contact us within 24 hours—we'll return and make it right. No questions, no cost.
          </p>
        </div>
      </section>

      {/* Trust Points */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Why Homeowners Trust EstherSpotless</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {trustPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <point.icon className="w-8 h-8 text-green-500 flex-shrink-0" />
                <p className="text-gray-700 text-lg">{point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">What Our Customers Say</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-lg mb-4 italic">"{testimonial.text}"</p>
                <p className="text-gray-600 font-semibold">— {testimonial.author}, {testimonial.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">About EstherSpotless</h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-4">
            Founded on a simple truth: Perfection is a prerequisite for peace.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-2">
            We're a local Ottawa cleaning company built on care, precision, and integrity. Every visit is a promise—to treat your space with the same respect and detail we'd give our own homes.
          </p>
          <p className="text-lg text-gray-600 font-semibold flex items-center justify-center gap-2">
            <MapPin className="w-5 h-5" />
            Proudly serving Ottawa and surrounding areas
          </p>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-12">Get in Touch</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <a href="mailto:hello@estherspotless.ca" className="flex flex-col items-center gap-3 hover:scale-110 transition-transform">
              <Mail className="w-12 h-12" />
              <span className="text-lg">hello@estherspotless.ca</span>
            </a>
            <a href="tel:6135550192" className="flex flex-col items-center gap-3 hover:scale-110 transition-transform">
              <Phone className="w-12 h-12" />
              <span className="text-lg">(613) 805-5371</span>
            </a>
            <div className="flex flex-col items-center gap-3">
              <MapPin className="w-12 h-12" />
              <span className="text-lg">Ottawa, Ontario</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-purple-400" />
            <span className="text-xl font-bold">EstherSpotless</span>
          </div>
          <p className="text-gray-400">© 2025 EstherSpotless. Perfection is Always in Season.</p>
        </div>
      </footer>
    </div>
  );
}